import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, SectionHeading } from "@/components/site/Reveal";

const ParallaxImage = ({ src, alt, testid }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);
  return (
    <div ref={ref} className="clipped-frame overflow-hidden">
      <motion.img
        src={src}
        alt={alt}
        data-testid={testid}
        style={{ y }}
        className="h-[420px] w-full bg-[#080808] object-contain md:h-[520px]"
      />
    </div>
  );
};

export default function About({ settings }) {
  const stats = settings.stats || [];
  const chapters = settings.chapters || [];

  return (
    <section id="about" data-testid="about-section" className="relative mx-auto max-w-7xl px-6 py-28 lg:px-10 lg:py-36">
      <SectionHeading kicker="The House" title={settings.about_heading || "A Legacy Crowned in Gold"} testid="about-heading" />

      <div className="mb-24 grid grid-cols-2 gap-x-6 gap-y-12 lg:grid-cols-4">
        {stats.map((s, i) => (
          <Reveal key={i} delay={i * 0.1} className="border-l border-[#D4AF37]/30 pl-6">
            <div data-testid={`stat-value-${i}`} className="font-display gold-gradient-text text-5xl font-medium md:text-6xl">
              {s.value}
            </div>
            <div data-testid={`stat-label-${i}`} className="mt-2 text-[11px] uppercase tracking-[0.25em] text-neutral-400">
              {s.label}
            </div>
          </Reveal>
        ))}
      </div>

      <div className="space-y-28">
        {chapters.map((ch, i) => (
          <div
            key={i}
            className={`grid items-center gap-12 lg:grid-cols-2 lg:gap-20 ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}
          >
            <Reveal>
              <div className="flex items-baseline gap-5">
                <span className="font-display text-6xl font-light text-[#D4AF37]/40 md:text-7xl">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 data-testid={`chapter-title-${i}`} className="font-display text-3xl font-medium text-white md:text-4xl">
                  {ch.title}
                </h3>
              </div>
              <p data-testid={`chapter-text-${i}`} className="mt-6 max-w-xl text-sm font-light leading-loose tracking-wide text-neutral-300 md:text-base">
                {ch.text}
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <ParallaxImage src={ch.image} alt={ch.title} testid={`chapter-image-${i}`} />
            </Reveal>
          </div>
        ))}
      </div>

      <div className="mt-32 grid items-center gap-12 border-t border-white/10 pt-24 lg:grid-cols-5 lg:gap-16">
        <Reveal className="lg:col-span-2">
          <div className="clipped-frame overflow-hidden">
            <img
              src={settings.founder_image || "/brand/valentina-hero.jpg"}
              alt={settings.founder_name}
              data-testid="founder-image"
              className="h-[540px] w-full bg-[#080808] object-contain transition-transform duration-700 hover:scale-105"
            />
          </div>
        </Reveal>
        <Reveal delay={0.15} className="lg:col-span-3">
          <span className="font-script text-3xl text-[#D4AF37]">The Founder</span>
          <h3 data-testid="founder-name" className="font-display mt-3 text-4xl font-medium text-white md:text-5xl">
            {settings.founder_name}
          </h3>
          <p data-testid="founder-title" className="mt-3 text-xs uppercase tracking-[0.3em] text-[#D4AF37]/80">
            {settings.founder_title}
          </p>
          <p data-testid="founder-bio" className="mt-8 max-w-2xl text-sm font-light leading-loose tracking-wide text-neutral-300 md:text-base">
            {settings.founder_bio}
          </p>
        </Reveal>
      </div>
    </section>
  );
}
